import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaCheck, FaTimes, FaEye, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { listingService } from '../services/listingService';
import { supabase } from '../lib/supabase';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  color: #8B5A96;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Stats = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem 2rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  text-align: center;
  
  h3 {
    color: #8B5A96;
    margin: 0;
    font-size: 2rem;
  }
  
  p {
    margin: 0.5rem 0 0 0;
    color: #666;
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.85rem;
    }
  }
`;

const PendingListings = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const ListingCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  border-left: 4px solid #FFC107;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ListingHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ListingTitle = styled.h3`
  color: #333;
  margin: 0 0 0.5rem 0;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ListingMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const ListingDescription = styled.p`
  color: #666;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const FullStorySection = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 1.5rem;
  
  h4 {
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }
  
  p {
    color: #666;
    line-height: 1.5;
    margin: 0;
  }
`;

const ListingDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 5px;
  
  strong {
    color: #333;
    display: block;
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }
  
  span {
    color: #666;
    font-size: 0.9rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
`;

const ApproveButton = styled(ActionButton)`
  background: #28a745;
  color: white;
  
  &:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-2px);
  }
`;

const RejectButton = styled(ActionButton)`
  background: #dc3545;
  color: white;
  
  &:hover:not(:disabled) {
    background: #c82333;
    transform: translateY(-2px);
  }
`;

const ViewButton = styled(ActionButton)`
  background: #6c757d;
  color: white;
  
  &:hover:not(:disabled) {
    background: #5a6268;
    transform: translateY(-2px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #8B5A96;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  
  h3 {
    color: #333;
    margin-bottom: 1rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  
  h3 {
    color: #8B5A96;
    margin-bottom: 1rem;
  }
  
  textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    min-height: 100px;
    resize: vertical;
    margin-bottom: 1rem;
  }
  
  div {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
  }
`;

const AdminModeration = () => {
  const { profile } = useAuth();
  const [pendingListings, setPendingListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, total: 0 });
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState({ show: false, listing: null, reason: '' });

  useEffect(() => {
    if (profile?.role_type === 'admin') {
      loadPendingListings();
      loadStats();
    }
  }, [profile]);

  const loadPendingListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          profiles!owner_id (
            display_name,
            email
          )
        `)
        .eq('status', 'pending_moderation')
        .order('submitted_at', { ascending: true });

      if (error) throw error;
      setPendingListings(data || []);
    } catch (error) {
      console.error('Error loading pending listings:', error);
      toast.error('Failed to load pending listings');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data: pending, error: pendingError } = await supabase
        .from('listings')
        .select('id', { count: 'exact' })
        .eq('status', 'pending_moderation');

      const { data: total, error: totalError } = await supabase
        .from('listings')
        .select('id', { count: 'exact' });

      if (pendingError || totalError) throw pendingError || totalError;

      setStats({
        pending: pending?.length || 0,
        total: total?.length || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleApprove = async (listing) => {
    setActionLoading(listing.id);
    try {
      // Update listing status
      const { error: listingError } = await supabase
        .from('listings')
        .update({
          status: 'active',
          approved_at: new Date().toISOString()
        })
        .eq('id', listing.id);

      if (listingError) throw listingError;

      // Create moderation decision
      const { error: decisionError } = await supabase
        .from('moderation_decisions')
        .insert({
          listing_id: listing.id,
          moderator_id: profile.id,
          status: 'approved'
        });

      if (decisionError) throw decisionError;

      toast.success('Listing approved successfully!');
      
      // Remove from pending list
      setPendingListings(prev => prev.filter(l => l.id !== listing.id));
      setStats(prev => ({ ...prev, pending: prev.pending - 1 }));
      
    } catch (error) {
      console.error('Error approving listing:', error);
      toast.error('Failed to approve listing');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.listing || !rejectModal.reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setActionLoading(rejectModal.listing.id);
    try {
      // Update listing status
      const { error: listingError } = await supabase
        .from('listings')
        .update({ status: 'rejected' })
        .eq('id', rejectModal.listing.id);

      if (listingError) throw listingError;

      // Create moderation decision
      const { error: decisionError } = await supabase
        .from('moderation_decisions')
        .insert({
          listing_id: rejectModal.listing.id,
          moderator_id: profile.id,
          status: 'rejected',
          reason: rejectModal.reason
        });

      if (decisionError) throw decisionError;

      toast.success('Listing rejected');
      
      // Remove from pending list
      setPendingListings(prev => prev.filter(l => l.id !== rejectModal.listing.id));
      setStats(prev => ({ ...prev, pending: prev.pending - 1 }));
      
      // Close modal
      setRejectModal({ show: false, listing: null, reason: '' });
      
    } catch (error) {
      console.error('Error rejecting listing:', error);
      toast.error('Failed to reject listing');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (listing) => {
    setRejectModal({ show: true, listing, reason: '' });
  };

  const closeRejectModal = () => {
    setRejectModal({ show: false, listing: null, reason: '' });
  };

  if (profile?.role_type !== 'admin') {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
          <FaExclamationTriangle size={48} color="#8B5A96" />
          <h2>Access Denied</h2>
          <p>You need admin privileges to access this page.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Content Moderation</Title>
        <p>Review and approve pending listings</p>
      </Header>

      <Stats>
        <StatCard>
          <h3>{stats.pending}</h3>
          <p>Pending Review</p>
        </StatCard>
        <StatCard>
          <h3>{stats.total}</h3>
          <p>Total Listings</p>
        </StatCard>
      </Stats>

      {loading ? (
        <LoadingContainer>
          <FaSpinner size={24} />
          <span style={{ marginLeft: '1rem' }}>Loading pending listings...</span>
        </LoadingContainer>
      ) : pendingListings.length === 0 ? (
        <EmptyState>
          <FaCheck size={48} color="#28a745" />
          <h3>All Caught Up!</h3>
          <p>No listings are currently pending review.</p>
        </EmptyState>
      ) : (
        <PendingListings>
          {pendingListings.map(listing => (
            <ListingCard key={listing.id}>
              <ListingHeader>
                <div>
                  <ListingTitle>{listing.title}</ListingTitle>
                  <ListingMeta>
                    <span>By: {listing.profiles.display_name}</span>
                    <span>•</span>
                    <span>Submitted: {new Date(listing.submitted_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>Type: {listing.type}</span>
                  </ListingMeta>
                </div>
              </ListingHeader>

              <ListingDescription>
                {listing.short_description}
              </ListingDescription>

              {listing.full_story && (
                <FullStorySection>
                  <h4>Full Story</h4>
                  <p>{listing.full_story}</p>
                </FullStorySection>
              )}

              <ListingDetails>
                {listing.type === 'hustle' && (
                  <DetailItem>
                    <strong>Industry</strong>
                    <span>{listing.industry || 'Not specified'}</span>
                  </DetailItem>
                )}
                <DetailItem>
                  <strong>Location</strong>
                  <span>
                    {listing.location?.city || 'Not specified'}
                    {listing.location?.province && `, ${listing.location.province}`}
                  </span>
                </DetailItem>
                {listing.type === 'hustle' && (
                  <>
                    <DetailItem>
                      <strong>Team Size</strong>
                      <span>{listing.size || 'Not specified'}</span>
                    </DetailItem>
                    <DetailItem>
                      <strong>Role Type</strong>
                      <span>{listing.role || 'Not specified'}</span>
                    </DetailItem>
                  </>
                )}
                <DetailItem>
                  <strong>Work Structure</strong>
                  <span>{listing.remote || 'Not specified'}</span>
                </DetailItem>
                <DetailItem>
                  <strong>Traveling</strong>
                  <span>{listing.traveling ? 'Yes' : 'No'}</span>
                </DetailItem>
                <DetailItem>
                  <strong>Skills Required</strong>
                  <span>{listing.skills?.join(', ') || 'Not specified'}</span>
                </DetailItem>
              </ListingDetails>

              <ActionButtons>
                <ApproveButton 
                  onClick={() => handleApprove(listing)}
                  disabled={actionLoading === listing.id}
                >
                  {actionLoading === listing.id ? (
                    <FaSpinner />
                  ) : (
                    <FaCheck />
                  )}
                  Approve
                </ApproveButton>
                
                <RejectButton 
                  onClick={() => openRejectModal(listing)}
                  disabled={actionLoading === listing.id}
                >
                  <FaTimes />
                  Reject
                </RejectButton>
              </ActionButtons>
            </ListingCard>
          ))}
        </PendingListings>
      )}

      {rejectModal.show && (
        <Modal onClick={closeRejectModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Reject Listing</h3>
            <p>Please provide a reason for rejecting this listing:</p>
            <textarea
              placeholder="Enter rejection reason..."
              value={rejectModal.reason}
              onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
            />
            <div>
              <ActionButton onClick={closeRejectModal}>
                Cancel
              </ActionButton>
              <RejectButton 
                onClick={handleReject}
                disabled={!rejectModal.reason.trim() || actionLoading}
              >
                {actionLoading ? <FaSpinner /> : <FaTimes />}
                Reject
              </RejectButton>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminModeration;